import { LANGUAGE_STORAGE_KEY } from "@frontend-shared/constants";
import type { IAppResources } from "@frontend-shared/i18n";
import { defaultLanguage, RESOURCES } from "@frontend-shared/i18n";
import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import { FLAVOR_CONFIG } from "@/lib/flavor-config";

const setDocumentLanguage = (language: string): void => {
  document.documentElement.setAttribute("lang", language);
  document.documentElement.setAttribute("dir", i18n.dir(language));
};

export { RESOURCES } from "@frontend-shared/i18n";

// NOTE: Get language from localStorage or use default (guard for SSR/test environments)
const savedLanguage =
  typeof localStorage !== "undefined" &&
  typeof localStorage.getItem === "function"
    ? localStorage.getItem(LANGUAGE_STORAGE_KEY)
    : null;

const appDefaultLanguage = FLAVOR_CONFIG.defaultLanguage;

const initialLanguage =
  savedLanguage && Object.keys(RESOURCES).includes(savedLanguage)
    ? savedLanguage
    : appDefaultLanguage;

// NOTE: Guard for SSR/test environments where document may not exist
if (typeof document !== "undefined") {
  setDocumentLanguage(initialLanguage);
}

i18n.use(initReactI18next).init({
  resources: RESOURCES,
  lng: initialLanguage,
  fallbackLng: defaultLanguage,
  debug: false,
  interpolation: {
    escapeValue: false,
  },
  ns: Object.keys(RESOURCES[defaultLanguage]),
  defaultNS: "common",
  returnNull: false,
  parseMissingKeyHandler: (key): string => {
    console.error("parseMissingKeyHandler", key);
    return key;
  },
});

// NOTE: Store language in localStorage when changed
i18n.on("languageChanged", (lng) => {
  if (
    typeof localStorage !== "undefined" &&
    typeof localStorage.setItem === "function"
  ) {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  }
  setDocumentLanguage(lng);
});

declare module "i18next" {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- i18next requires exact interface name
  interface CustomTypeOptions {
    defaultNS: "common";
    // NOTE: NSAI-5266 — shared resource type (the generated `pipedreamTools` namespace
    // is typed as a dynamic Record there) so this augmentation matches frontend-shared's.
    resources: IAppResources;
    strictKeyChecks: true;
  }
}

export default i18n;
